import React, { useEffect, useState, Children, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, BookOpen, Headphones, Gift, ShieldCheck, Clock, Lock, ChevronDown, ChevronUp, Sparkles, Zap, Heart, Target, RefreshCw, Eye } from 'lucide-react';
import { playerTypes, rootCodes, painLabels, commitLabels, goalLabels, testimonials, faqItems } from '../data/resultsData';
import type { PlayerType, RootCode } from '../data/resultsData';
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
interface OfferPageProps {
  playerType: PlayerType;
  rootCode: RootCode;
  painTag: string | null;
  commitLevel: string | null;
  primaryGoal: string | null;
  recommendedOffer: 17 | 37;
  onCTAClick?: () => void;
}
function GoldButton({
  children,
  large,
  scrollToPricing = true,
  href,
  onCTAClick
}: {
  children: React.ReactNode;
  large?: boolean;
  scrollToPricing?: boolean;
  href?: string;
  onCTAClick?: () => void;
}) {
  const handleClick = () => {
    if (onCTAClick) onCTAClick();
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (scrollToPricing) {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        pricingSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };
  return <button onClick={handleClick} className={`group inline-flex items-center justify-center gap-2.5 rounded-lg bg-gradient-to-r from-matrix-gold to-matrix-gold-bright font-body font-semibold uppercase tracking-wider text-matrix-bg shadow-[0_0_32px_rgba(201,168,76,0.2)] transition-all duration-300 hover:shadow-[0_0_48px_rgba(201,168,76,0.35)] hover:scale-[1.02] active:scale-[0.98] w-full ${large ? 'px-8 py-4 text-sm' : 'px-6 py-3.5 text-xs'}`} data-id="element-114">
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" data-id="element-115" />
    </button>;
}
function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <div className="space-y-3" data-id="element-116">
      {faqItems.map((item, i) => <div key={i} className="rounded-xl border border-matrix-border/50 bg-matrix-card/40 overflow-hidden" data-id="element-117">
          <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="flex w-full items-center justify-between px-5 py-4 text-left" data-id="element-118">
            <span className="font-body text-[15px] font-medium text-matrix-text pr-4" data-id="element-119">
              {item.question}
            </span>
            {openIndex === i ? <ChevronUp className="h-4 w-4 flex-shrink-0 text-matrix-gold" data-id="element-120" /> : <ChevronDown className="h-4 w-4 flex-shrink-0 text-matrix-text-dim" data-id="element-121" />}
          </button>
          <motion.div initial={false} animate={{
        height: openIndex === i ? 'auto' : 0,
        opacity: openIndex === i ? 1 : 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="overflow-hidden" data-id="element-122">
            <p className="px-5 pb-4 font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-123">
              {item.answer}
            </p>
          </motion.div>
        </div>)}
    </div>;
}
export function OfferPage({
  playerType,
  rootCode,
  painTag,
  commitLevel,
  primaryGoal,
  recommendedOffer,
  onCTAClick
}: OfferPageProps) {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Facebook Pixel
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Load FB Pixel script if not already loaded
    if (!(window as any).fbq) {
      const f = window as any;
      const n = f.fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    ;
    (window as any).fbq('init', '1398555731600742');
    (window as any).fbq('track', 'PageView');
  }, []);
  // Load Wistia scripts
  useEffect(() => {
    const loadScript = (src: string, type?: string) => {
      if (document.querySelector(`script[src="${src}"]`)) return;
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      if (type) script.type = type;
      document.head.appendChild(script);
    };
    loadScript('https://fast.wistia.com/player.js');
    loadScript('https://fast.wistia.com/embed/1rnyt8boov.js', 'module');
  }, []);
  const typeData = playerTypes[playerType];
  const codeData = rootCodes[rootCode];
  const painLabel = painTag ? painLabels[painTag] || 'ruido' : 'ruido';
  const commitLabel = commitLevel ? commitLabels[commitLevel] || '' : '';
  const goalLabel = primaryGoal ? goalLabels[primaryGoal] || '' : '';
  const is37 = recommendedOffer === 37;
  return <div className="min-h-screen bg-matrix-bg px-6 py-12 md:px-5 md:py-20" data-id="element-124">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="mx-auto max-w-2xl" data-id="element-125">
        {/* Block 1 — Hero */}
        <motion.div variants={fadeUp} className="text-center" data-id="element-126">
          <p className="font-body text-sm text-matrix-gold-muted md:text-sm" data-id="element-127">
            Basado en tus respuestas del test…
          </p>
          <h1 className="mt-4 font-heading text-[1.65rem] font-bold leading-snug text-matrix-text md:text-4xl md:leading-tight" data-id="element-128">
            <span className="text-matrix-gold" data-id="element-129">{typeData.title}</span>, tu
            salida no es "otro método".{' '}
            <span className="text-matrix-text" data-id="element-130">Es tu Secuencia.</span>
          </h1>

          {/* Wistia Video */}
          <div className="mt-6 rounded-xl overflow-hidden" data-id="element-131">
            <div ref={el => {
            if (el && !el.querySelector('wistia-player')) {
              const style = document.createElement('style');
              style.textContent = "wistia-player[media-id='1rnyt8boov']:not(:defined){background:center/contain no-repeat url('https://fast.wistia.com/embed/medias/1rnyt8boov/swatch');display:block;filter:blur(5px);padding-top:56.25%}";
              el.appendChild(style);
              const player = document.createElement('wistia-player');
              player.setAttribute('media-id', '1rnyt8boov');
              player.setAttribute('aspect', '1.7777777777777777');
              el.appendChild(player);
            }
          }} data-id="element-132" />
          </div>

          <p className="mt-5 font-body text-base leading-relaxed text-matrix-text-muted md:text-base" data-id="element-133">
            Tu Código Raíz ahora mismo es:{' '}
            <span className="font-semibold text-matrix-gold" data-id="element-134">
              {codeData.name}
            </span>
            . Por eso, aunque tengas consciencia, sigues sintiendo{' '}
            <span className="text-matrix-text" data-id="element-135">{painLabel}</span>. La buena
            noticia: puedes empezar hoy con un manual corto, directo y
            ejecutable.
          </p>

          <div className="mt-8" data-id="element-136">
            <GoldButton large onCTAClick={onCTAClick} data-id="element-137">
              Sí — Quiero mi plan completo
            </GoldButton>
          </div>
          <p className="mt-3 font-body text-xs text-matrix-text-dim md:text-xs" data-id="element-138">
            Acceso inmediato &bull; eBook + Audiolibro &bull; Garantía de
            devolución
          </p>
        </motion.div>

        {/* Block 2 — This is for you if */}
        <motion.div variants={fadeUp} className="mt-16" data-id="element-139">
          <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text md:text-2xl" data-id="element-140">
            Esto fue creado para ti si…
          </h2>
          <ul className="mt-5 space-y-3.5" data-id="element-141">
            {['Estás cansado/a de sentir que "hay algo más" pero no logras sostenerlo', 'Ya probaste cosas (meditación, manifestación, terapia, energía) y sigues en loop', 'Quieres despertar sin perderte a ti mismo/a y sin convertir tu vida en un ritual infinito', 'Quieres un marco que una consciencia + acción diaria (no solo teoría)'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-142">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-matrix-gold" data-id="element-143" />
                <span className="font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-144">
                  {item}
                </span>
              </li>)}
          </ul>
        </motion.div>

        {/* Block 3 — Problem agitation */}
        <motion.div variants={fadeUp} className="mt-16" data-id="element-145">
          <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text md:text-2xl" data-id="element-146">
            La trampa no es que estés "mal".
            <br data-id="element-147" />
            <span className="text-matrix-gold" data-id="element-148">
              La trampa es que estás reaccionando al tablero.
            </span>
          </h2>
          <p className="mt-4 font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-149">
            La mayoría de personas viven así:
          </p>
          <div className="mt-4 space-y-3" data-id="element-150">
            {[{
            icon: RefreshCw,
            text: 'Se activan → buscan una respuesta → se calman 2 días → vuelven al mismo patrón'
          }, {
            icon: Target,
            text: 'Persiguen éxito → sienten vacío → se culpan → repiten'
          }, {
            icon: Sparkles,
            text: 'Consumen "sanación" → se saturan → se confunden → se distraen'
          }].map((item, i) => <div key={i} className="flex items-start gap-3 rounded-xl border border-matrix-border/30 bg-matrix-card/30 px-4 py-3.5" data-id="element-151">
                <item.icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-matrix-gold-muted" data-id="element-152" />
                <span className="font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-153">
                  {item.text}
                </span>
              </div>)}
          </div>
          <p className="mt-5 font-heading text-base italic text-matrix-text/90" data-id="element-154">
            Tú no necesitas más presión. Necesitas ver el código correcto.
          </p>
        </motion.div>

        {/* Block 4 — Mechanism */}
        <motion.div variants={fadeUp} className="mt-16" data-id="element-155">
          <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text md:text-2xl" data-id="element-156">
            El cambio ocurre cuando dejas de pelear con tu vida…
            <br data-id="element-157" />
            <span className="text-matrix-gold" data-id="element-158">
              y aprendes a jugar consciente.
            </span>
          </h2>
          <p className="mt-4 font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-159">
            En este manual vas a encontrar un marco claro:
          </p>
          <div className="mt-5 space-y-3" data-id="element-160">
            {[{
            title: 'El Velo Revelado',
            desc: 'Por qué olvidaste quién eres (y cómo se sostiene el olvido)'
          }, {
            title: 'Las Reglas Ocultas del Sistema',
            desc: 'Miedo, tiempo e identidad como "programas"'
          }, {
            title: 'La Secuencia del Despertar',
            desc: 'Pasos concretos para reescribir tu experiencia diaria'
          }, {
            title: 'El Plan del Juego Infinito',
            desc: 'Crear desde amor y elección, no desde supervivencia'
          }].map((item, i) => <div key={i} className="rounded-xl border border-matrix-gold/15 bg-matrix-gold/5 px-5 py-4" data-id="element-161">
                <h4 className="font-heading text-[15px] font-semibold text-matrix-gold" data-id="element-162">
                  {item.title}
                </h4>
                <p className="mt-1.5 font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-163">
                  {item.desc}
                </p>
              </div>)}
          </div>
          <p className="mt-5 font-body text-[15px] leading-relaxed text-matrix-text-muted italic" data-id="element-164">
            Cuando tienes secuencia, dejas de saltar de método en método.
            Empiezas a notar resultados porque sabes qué mover primero.
          </p>
        </motion.div>

        {/* Block 5 — What you get */}
        <motion.div variants={fadeUp} className="mt-16" data-id="element-165">
          <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text md:text-2xl" data-id="element-166">
            Lo que recibes hoy
          </h2>
          <div className="mt-5 space-y-4" data-id="element-167">
            <div className="rounded-xl border border-matrix-border/50 bg-matrix-card/60 p-5" data-id="element-168">
              <div className="flex items-center gap-3 mb-2" data-id="element-169">
                <BookOpen className="h-5 w-5 text-matrix-gold" data-id="element-170" />
                <h4 className="font-body text-[15px] font-semibold text-matrix-text" data-id="element-171">
                  Nuevo Manual de Consciencia (Edición Digital)
                </h4>
              </div>
              <ul className="ml-8 space-y-1.5" data-id="element-172">
                <li className="font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-173">
                  Lectura inmediata en cualquier dispositivo
                </li>
                <li className="font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-174">
                  Estructurado para leerlo rápido y aplicarlo
                </li>
              </ul>
            </div>
            <div className="rounded-xl border border-matrix-border/50 bg-matrix-card/60 p-5" data-id="element-175">
              <div className="flex items-center gap-3 mb-2" data-id="element-176">
                <Headphones className="h-5 w-5 text-matrix-gold" data-id="element-177" />
                <h4 className="font-body text-[15px] font-semibold text-matrix-text" data-id="element-178">
                  Audiolibro completo incluido
                </h4>
              </div>
              <p className="ml-8 font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-179">
                Ideal si eres {typeData.title} y necesitas integración diaria
                (caminar, manejar, antes de dormir)
              </p>
            </div>
            <div className="rounded-xl border border-matrix-gold/20 bg-matrix-gold/5 p-5" data-id="element-180">
              <div className="flex items-center gap-3 mb-2" data-id="element-181">
                <Gift className="h-5 w-5 text-matrix-gold" data-id="element-182" />
                <h4 className="font-body text-[15px] font-semibold text-matrix-gold" data-id="element-183">
                  BONUS: DMT — La Molécula Espiritual (Audio + eBook)
                </h4>
              </div>
              <p className="ml-8 font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-184">
                Descubre la molécula que une ciencia y espíritu. Una exploración
                profunda de la consciencia, la percepción y las dimensiones
                ocultas de la realidad — combinando conocimiento científico con
                profundidad espiritual.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Block 6 — Personalized recommendation */}
        <motion.div variants={fadeUp} className="mt-16" id="pricing-section" data-id="element-185">
          <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text text-center md:text-2xl" data-id="element-186">
            Tu recomendación personalizada
          </h2>

          {is37 ? <>
              {/* $37 recommended */}
              <div className="mt-6 relative rounded-2xl border-2 border-matrix-gold/40 bg-gradient-to-br from-matrix-card to-matrix-bg p-6 md:p-8 shadow-[0_0_40px_rgba(201,168,76,0.08)]" data-id="element-187">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-matrix-gold px-4 py-1 whitespace-nowrap shadow-sm" data-id="element-188">
                  <span className="font-body text-xs font-bold uppercase tracking-wider text-matrix-bg" data-id="element-189">
                    Recomendado para ti
                  </span>
                </div>

                <div className="mb-6 overflow-hidden rounded-xl" data-id="element-190">
                  <img src="https://cdn.magicpatterns.com/uploads/d5bBfJUmRu45YXZN8XqJ2k/BUNDLE.png" alt="Pack Despertar" className="w-full h-auto object-cover" data-id="element-191" />
                </div>

                <h3 className="mt-4 font-heading text-xl font-bold text-matrix-text text-center" data-id="element-192">
                  Pack Despertar
                </h3>
                <p className="text-center font-body text-[15px] text-matrix-text-muted" data-id="element-193">
                  Mejor para {typeData.title}
                </p>
                <p className="mt-3 text-center" data-id="element-194">
                  <span className="font-heading text-4xl font-bold text-matrix-gold" data-id="element-195">
                    $37
                  </span>
                  <span className="ml-2 font-body text-sm text-matrix-text-dim" data-id="element-196">
                    hoy
                  </span>
                </p>

                <ul className="mt-5 space-y-2.5" data-id="element-197">
                  {['Manual de Consciencia (eBook digital)', 'Audiolibro completo', 'BONUS: DMT — La Molécula Espiritual (Audio + eBook)', 'Meditaciones guiadas / audio binaural (para aplicar la Secuencia sin esfuerzo mental)'].map((item, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-198">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-matrix-green" data-id="element-199" />
                      <span className="font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-200">
                        {item}
                      </span>
                    </li>)}
                </ul>

                {commitLabel && <p className="mt-4 rounded-lg bg-matrix-gold/5 border border-matrix-gold/10 px-4 py-3 font-body text-sm leading-relaxed text-matrix-text-muted" data-id="element-201">
                    Como dijiste que puedes comprometerte al menos{' '}
                    <span className="text-matrix-gold font-medium" data-id="element-202">
                      {commitLabel}/día
                    </span>
                    , este pack te da la parte que más acelera el cambio: la
                    práctica guiada.
                  </p>}

                <div className="mt-6" data-id="element-203">
                  <GoldButton large href="https://www.voicesoffreedomus.com/cart/45290003792058:1" onCTAClick={onCTAClick} data-id="element-204">
                    Sí! Quiero el Pack Completo
                  </GoldButton>
                </div>
                <p className="mt-3 text-center font-body text-xs text-matrix-text-dim" data-id="element-205">
                  Acceso instantáneo &bull; Garantía &bull; Descarga inmediata
                </p>
              </div>

              {/* $17 alternative */}
              <div className="mt-6 rounded-xl border border-matrix-border/40 bg-matrix-card/30 p-5" data-id="element-206">
                <div className="mb-4 overflow-hidden rounded-lg" data-id="element-207">
                  <img src="https://cdn.magicpatterns.com/uploads/35dbXjq45XTCUJCZck2zxa/GeneratedImageOctober30_2025-1_01AM.png" alt="Edición Digital" className="w-full h-auto object-cover" data-id="element-208" />
                </div>
                <h4 className="font-body text-[15px] font-semibold text-matrix-text text-center" data-id="element-209">
                  Edición Digital + Audiolibro + Bonus DMT —{' '}
                  <span className="text-matrix-gold" data-id="element-210">$17</span> hoy
                </h4>
                <ul className="mt-3 space-y-2" data-id="element-211">
                  {['Manual de Consciencia (eBook digital)', 'Audiolibro completo', 'BONUS: DMT — La Molécula Espiritual (Audio + eBook)'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-212">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-matrix-gold-muted" data-id="element-213" />
                      <span className="font-body text-sm leading-relaxed text-matrix-text-muted" data-id="element-214">
                        {item}
                      </span>
                    </li>)}
                </ul>
                <div className="mt-4" data-id="element-215">
                  <button onClick={() => {
                if (onCTAClick) onCTAClick();
                window.open('https://www.voicesoffreedomus.com/cart/45280183124154:1', '_blank', 'noopener,noreferrer');
              }} className="group w-full inline-flex items-center justify-center gap-2 rounded-lg border border-matrix-gold/30 bg-transparent px-6 py-3 font-body text-sm font-semibold uppercase tracking-wider text-matrix-gold transition-all duration-300 hover:bg-matrix-gold/5 active:scale-[0.98]" data-id="element-216">
                    Empezar con la edición digital
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-217" />
                  </button>
                </div>
                <p className="mt-3 text-center font-body text-xs text-matrix-text-dim" data-id="element-218">
                  Si luego quieres las meditaciones guiadas para acelerar,
                  podrás mejorar tu acceso.
                </p>
              </div>
            </> : <>
              {/* $17 primary */}
              <div className="mt-6 rounded-2xl border border-matrix-gold/30 bg-gradient-to-br from-matrix-card to-matrix-bg p-6 md:p-8" data-id="element-219">
                <div className="mb-6 overflow-hidden rounded-xl" data-id="element-220">
                  <img src="https://cdn.magicpatterns.com/uploads/35dbXjq45XTCUJCZck2zxa/GeneratedImageOctober30_2025-1_01AM.png" alt="Edición Digital" className="w-full h-auto object-cover" data-id="element-221" />
                </div>
                <h3 className="font-heading text-xl font-bold text-matrix-text text-center" data-id="element-222">
                  Edición Digital + Audiolibro
                </h3>
                <p className="mt-3 text-center" data-id="element-223">
                  <span className="font-heading text-4xl font-bold text-matrix-gold" data-id="element-224">
                    $17
                  </span>
                  <span className="ml-2 font-body text-sm text-matrix-text-dim" data-id="element-225">
                    hoy
                  </span>
                </p>
                <ul className="mt-5 space-y-2.5" data-id="element-226">
                  {['Manual de Consciencia (eBook digital)', 'Audiolibro completo', 'BONUS: DMT — La Molécula Espiritual (Audio + eBook)'].map((item, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-227">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-matrix-green" data-id="element-228" />
                      <span className="font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-229">
                        {item}
                      </span>
                    </li>)}
                </ul>
                <div className="mt-6" data-id="element-230">
                  <GoldButton large onCTAClick={onCTAClick} data-id="element-231">
                    Empezar con la edición digital
                  </GoldButton>
                </div>
              </div>

              {/* $37 upshift */}
              <div className="mt-6 rounded-xl border border-matrix-gold/15 bg-matrix-gold/5 p-5 text-center" data-id="element-232">
                <p className="font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-233">
                  Si quieres acelerar con meditaciones guiadas y audio binaural:
                </p>
                <button className="group mt-3 inline-flex items-center gap-2 font-body text-[15px] font-semibold text-matrix-gold transition-colors hover:text-matrix-gold-bright" data-id="element-234">
                  Ver Pack Despertar — $37
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-235" />
                </button>
              </div>
            </>}
        </motion.div>

        {/* Block 7 — Transformation */}
        <motion.div variants={fadeUp} className="mt-16" data-id="element-236">
          <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text md:text-2xl" data-id="element-237">
            Imagina tu próxima semana si tu código empieza a cambiar…
          </h2>
          <div className="mt-6 space-y-3" data-id="element-238">
            {[{
            before: 'Reaccionas al miedo',
            after: 'Eliges desde claridad',
            icon: Zap
          }, {
            before: 'Buscas señales con ansiedad',
            after: 'Ves sincronicidades con calma',
            icon: Eye
          }, {
            before: 'Te culpas por "no avanzar"',
            after: 'Sigues una secuencia simple',
            icon: Target
          }, {
            before: 'Sientes vacío',
            after: 'Sientes dirección interna real',
            icon: Heart
          }].map((item, i) => <div key={i} className="flex items-center gap-3 rounded-xl border border-matrix-border/30 bg-matrix-card/30 px-4 py-3.5" data-id="element-239">
                <item.icon className="h-4 w-4 flex-shrink-0 text-matrix-gold-muted" data-id="element-240" />
                <div className="font-body text-[15px]" data-id="element-241">
                  <span className="text-matrix-text-dim line-through" data-id="element-242">
                    {item.before}
                  </span>
                  <span className="mx-2 text-matrix-gold" data-id="element-243">→</span>
                  <span className="text-matrix-text font-medium" data-id="element-244">
                    {item.after}
                  </span>
                </div>
              </div>)}
          </div>
          <p className="mt-5 font-heading text-base italic text-matrix-text/90 text-center" data-id="element-245">
            No se trata de "volverte alguien nuevo". Se trata de recordar quién
            ya eres.
          </p>
        </motion.div>

        {/* Block 8 — Guarantee */}
        <motion.div variants={fadeUp} className="mt-16" data-id="element-246">
          <div className="rounded-2xl border border-matrix-gold/20 bg-matrix-card/40 p-6 md:p-8 text-center" data-id="element-247">
            <ShieldCheck className="mx-auto h-10 w-10 text-matrix-gold mb-4" data-id="element-248" />
            <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text" data-id="element-249">
              Garantía "Recuerda Quién Eres"
            </h2>
            <p className="mt-3 mx-auto max-w-md font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-250">
              Prueba el manual por 7 días. Si no sientes un cambio genuino en tu
              consciencia (más claridad, más paz, más dirección), envía un email
              a soporte con el asunto "Solicitud de Reembolso" y te devolvemos
              el dinero.
            </p>
            <p className="mt-4 font-heading text-[15px] italic text-matrix-gold" data-id="element-251">
              No arriesgas nada. El único riesgo es seguir jugando ciego.
            </p>
          </div>
        </motion.div>

        {/* Block 9 — Scarcity */}
        <motion.div variants={fadeUp} className="mt-16 text-center" data-id="element-252">
          <div className="inline-flex items-center gap-2 rounded-full border border-matrix-gold/20 bg-matrix-gold/5 px-4 py-1.5 mb-4" data-id="element-253">
            <Clock className="h-3.5 w-3.5 text-matrix-gold" data-id="element-254" />
            <span className="font-body text-sm font-medium text-matrix-gold" data-id="element-255">
              Precio de lanzamiento activo
            </span>
          </div>
          <p className="font-body text-[15px] leading-relaxed text-matrix-text-muted" data-id="element-256">
            Esta edición especial está con descuento ahora. Cuando cierre esta
            ventana, vuelve al precio regular.
          </p>
          <p className="mt-2 font-body text-sm italic text-matrix-text-dim" data-id="element-257">
            Cuanto antes recuerdes, antes cambia tu realidad. Retrasar solo
            alimenta el programa.
          </p>
        </motion.div>

        {/* Block 10 — Testimonials */}
        <motion.div variants={fadeUp} className="mt-16" data-id="element-258">
          <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text text-center md:text-2xl" data-id="element-259">
            Lo que dice la comunidad
          </h2>
          <div className="mt-6 space-y-4" data-id="element-260">
            {testimonials.map((t, i) => <div key={i} className="rounded-xl border border-matrix-border/40 bg-matrix-card/40 overflow-hidden" data-id="element-261">
                {/* @ts-ignore - image property exists in updated data file */}
                {t.image && <div className="w-full overflow-hidden" data-id="element-262">
                    <img
              // @ts-ignore
              src={t.image} alt={`Testimonio de ${t.author}`} className="w-full h-auto object-contain" data-id="element-263" />
                  </div>}
              </div>)}
          </div>
        </motion.div>

        {/* Block 11 — FAQ */}
        <motion.div variants={fadeUp} className="mt-16" data-id="element-264">
          <h2 className="font-heading text-[1.35rem] font-semibold text-matrix-text text-center mb-6 md:text-2xl" data-id="element-265">
            Preguntas frecuentes
          </h2>
          <FaqAccordion data-id="element-266" />
        </motion.div>

        {/* Block 12 — Final CTA */}
        <motion.div variants={fadeUp} className="mt-16 text-center pb-8" data-id="element-267">
          <h2 className="font-heading text-2xl font-bold text-matrix-text md:text-3xl" data-id="element-268">
            Tu plan está listo.
          </h2>
          <div className="mt-6 mx-auto max-w-sm" data-id="element-269">
            <GoldButton large href="https://www.voicesoffreedomus.com/cart/45290003792058:1" onCTAClick={onCTAClick} data-id="element-270">
              Sí! Quiero el Pack Completo
            </GoldButton>
          </div>
          <div className="mt-4 flex items-center justify-center gap-4 font-body text-sm text-matrix-text-dim" data-id="element-271">
            <span className="inline-flex items-center gap-1" data-id="element-272">
              <CheckCircle2 className="h-3 w-3 text-matrix-green" data-id="element-273" />
              Acceso instantáneo
            </span>
            <span className="inline-flex items-center gap-1" data-id="element-274">
              <Lock className="h-3 w-3 text-matrix-gold-muted" data-id="element-275" />
              Pago seguro
            </span>
            <span className="inline-flex items-center gap-1" data-id="element-276">
              <ShieldCheck className="h-3 w-3 text-matrix-gold-muted" data-id="element-277" />
              Garantía
            </span>
          </div>
          <p className="mt-6 font-heading text-base italic text-matrix-gold" data-id="element-278">
            Tu despertar no empieza cuando terminas el libro. Empieza cuando
            dices que sí.
          </p>
        </motion.div>
      </motion.div>
    </div>;
}